/**
 * Favorites Modal Component
 * Displays event schedules from favorite streamers
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, type FavoriteScheduleItem } from '../lib/api-client';
import { resolveMediaUrl } from '../lib/media-url';

interface FavoritesModalProps {
    isOpen: boolean;
    onClose: () => void;
    onViewStreamer?: (streamerUsername: string) => void;
    isInRoom?: boolean; // true when user is in live-room or peek-room
}

export default function FavoritesModal({
    isOpen,
    onClose,
    onViewStreamer,
    isInRoom = false,
}: FavoritesModalProps) {
    const { t } = useTranslation('common');
    const [schedules, setSchedules] = useState<FavoriteScheduleItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCannotMovePopup, setShowCannotMovePopup] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchSchedules();
        }
    }, [isOpen]);

    const fetchSchedules = async () => {
        setLoading(true);
        try {
            const response = await apiClient.getFavoriteSchedules();
            setSchedules(response.schedules);
        } catch (error) {
            console.error('Failed to fetch favorite schedules:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewStreamer = (streamerUsername: string) => {
        if (isInRoom) {
            setShowCannotMovePopup(true);
        } else {
            onViewStreamer?.(streamerUsername);
        }
    };

    // Get profile image URL
    const getProfileImageUrl = (imageUrl: string | null): string | null => {
        if (!imageUrl) return null;
        return resolveMediaUrl(imageUrl);
    };

    // Format date/time for display
    const formatDateTime = (dateString: string): string => {
        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${month}/${day} ${hours}:${minutes}`;
    };

    // Format time only
    const formatTime = (dateString: string): string => {
        const date = new Date(dateString);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-white w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <svg width="24" height="24" fill="currentColor" className="text-pink-500" viewBox="0 0 20 20">
                            <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
                        </svg>
                        {t('favorites.title')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="text-center py-12 text-gray-500">
                            {t('favorites.loading')}
                        </div>
                    ) : schedules.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <div className="text-5xl mb-4 opacity-50">📅</div>
                            <p>{t('favorites.noSchedules')}</p>
                            <p className="text-sm mt-2">{t('favorites.noSchedulesHint')}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {schedules.map((schedule) => {
                                const displayName = schedule.streamer_display_name || schedule.streamer_username;
                                const profileImage = getProfileImageUrl(schedule.streamer_profile_image_url);

                                return (
                                    <div
                                        key={schedule.schedule_id}
                                        className={`border overflow-hidden hover:shadow-md transition-all ${
                                            schedule.is_live
                                                ? 'border-pink-300 bg-pink-50'
                                                : 'border-gray-200 bg-white'
                                        }`}
                                    >
                                        <div className="flex items-center gap-4 p-4">
                                            {/* Profile Image */}
                                            <div className="w-14 h-14 flex-shrink-0 bg-gray-100 overflow-hidden">
                                                {profileImage ? (
                                                    <img
                                                        src={profileImage}
                                                        alt={displayName}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <span className="text-2xl">👩</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Schedule Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    {schedule.is_live && (
                                                        <span className="px-2 py-0.5 bg-pink-500 text-white text-xs font-bold animate-pulse">
                                                            {t('favorites.live')}
                                                        </span>
                                                    )}
                                                    <span className="font-bold text-gray-900 truncate">
                                                        {displayName}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-gray-600 flex items-center gap-1">
                                                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                    </svg>
                                                    <span>
                                                        {formatDateTime(schedule.scheduled_start)} 〜 {formatTime(schedule.scheduled_end)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <button
                                                onClick={() => handleViewStreamer(schedule.streamer_username)}
                                                disabled={!schedule.is_live}
                                                className={`px-4 py-2 text-sm font-semibold transition-colors ${
                                                    schedule.is_live
                                                        ? 'bg-pink-500 text-white hover:bg-pink-600'
                                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                }`}
                                            >
                                                {schedule.is_live ? t('favorites.watchStream') : t('favorites.waitingStream')}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-white border-2 border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                    >
                        {t('buttons.close')}
                    </button>
                </div>
            </div>

            {/* Cannot Move Popup */}
            {showCannotMovePopup && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
                    <div className="bg-white rounded-lg max-w-sm w-[90%] shadow-2xl overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex items-center gap-3">
                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-xl shadow">
                                ⚠️
                            </div>
                            <div className="text-lg font-bold text-white">{t('favorites.cannotMove.title')}</div>
                        </div>
                        {/* Content */}
                        <div className="px-6 py-6 text-gray-600 leading-relaxed text-center">
                            <p>{t('favorites.cannotMove.message')}</p>
                            <p className="text-sm text-gray-500 mt-3">
                                {t('favorites.cannotMove.hint')}
                            </p>
                        </div>
                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 flex justify-center">
                            <button
                                onClick={() => setShowCannotMovePopup(false)}
                                className="px-8 py-2.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-semibold rounded hover:from-pink-600 hover:to-pink-700 transition-all"
                            >
                                {t('buttons.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
